import React, { createContext, useContext } from 'react'
import { queryClient, useCandidate } from './CandidateProvider'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { cn } from '~/lib/utils'
import moment from 'moment'
import { useServiceRequests } from './ServicesSection'
import {
  CompletedOnStatus,
  CompletedStatus,
  CalloutStatus,
  PendingStatus,
} from './ResumeReviewDetail'
import RevisionDialog from './RevisionDialog'
import ServiceRequestFeedbackDialog from './ServiceRequestFeedbackDialog'
import ViewCoachFeedbackDialog from './ViewCoachFeedbackDialog'
import TabNav from '../shared/TabNav/TabNav'
import ButtonContainer from '../shared/TabNav/ButtonContainer'
import TabButton from '../shared/TabNav/TabButton'
import { PanelContainer, TabPanel } from '../shared/TabNav/index'
import NoShowDialog from './NoShowDialog'
import CancelMeetingModal from './CancelMeetingModal'
import { InitiateServiceRequestDialog } from './InitiateServiceRequestForm'
import RescheduleMeetingDialog from './RescheduleMeetingDialog'
import { Button } from '~/lib/ui/button'
import { useFormatTime } from '~/hooks/useFormatTime'
import { Loading } from '~/lib/ui/loading'

const researchQueueTypes = [
  'ServiceRequest::TargetCompanyResearch',
  'ServiceRequest::TargetedResearch',
  'ServiceRequest::SalaryResearch',
  'ServiceRequest::BusinessCardCreation',
]

const hasElectronicRequestTypes = [
  'ServiceRequest::Resume',
  'ServiceRequest::ResumeTailoring',
  'ServiceRequest::CoverLetterReview',
  'ServiceRequest::LinkedInReview',
]

export const ServiceRequestContext = createContext()
export const useServiceRequest = () => useContext(ServiceRequestContext) || {}

const formatDetailType = (detailType = '') =>
  detailType
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()

const ServiceIntroduction = () => {
  const { detailType } = useCandidate()
  switch (detailType) {
    case 'CareerPivotPlanning':
      return (
        <p>
          Premium Career Pivot Planning Calls are 50 minute meetings to discuss
          career change strategy. The candidate should provide information
          regarding their desired shift as part of the service request. This
          service is only provided once for each candidate.
        </p>
      )
    case 'OrientationCall':
      return (
        <p>
          Premium Introductory Calls are 50 minute meetings to allow the
          candidate to learn about their coach, the services that are available,
          and to share their goals for the program. This service is only
          provided once for each candidate. This call must be completed before
          they can schedule future calls.
        </p>
      )
    case 'MockInterview':
      return (
        <p>
          The Premium Mock Interview service includes a 50 minute meeting with
          the candidate. To request a mock interview the candidate must provide
          the job description and may also share their resume, the anticipated
          interview format, and anticipated interviewer roles. This service may
          be requested multiple times by the candidate. Each service request
          should have a separate feedback message to close the request. All mock
          interview requests for this candidate will be listed below.
        </p>
      )
    case 'NegotiationStrategy':
      return (
        <p>
          The Premium Negotiation Strategy service includes a 30 minute meeting
          with the candidate. To request a negotiation strategy call the
          candidate must provide information regarding their desired outcomes
          and any relevant background information. This service may be requested
          multiple times by the candidate. Each service request should have a
          separate feedback message to close the request. All negotiation
          strategy requests for this candidate will be listed below.
        </p>
      )
    case 'ResumeTailoring':
      return (
        <p>
          Resume Tailoring can be completed during a 30 minute meeting or
          electronically via the platform. The candidate will select their
          preferred mode and provide their current resume and target job
          description. This is a reoccurring service for candidates.
        </p>
      )
    case 'GeneralCheckIn':
      return (
        <p>
          The General Coach Check-In is a meeting-only service offered to Select
          candidates. This session complements specialized services (Resume
          Development, Interview Prep, LinkedIn, etc.), not replaces them. Focus
          on helping candidates clarify priorities, integrate insights from
          multiple services, and troubleshoot challenges. You are their sounding
          board. Use this time to address open questions, provide guidance, and
          surface needs that may map to other services. When feedback clearly
          belongs under a specific service, assign feedback under that service.
        </p>
      )
    case 'CliftonStrengths':
      return (
        <p>
          A Coach Lead will update the Candidate profile with their own unique
          CliftonStrengths assessment code. Encourage your candidate to complete
          the assessment before their next session. Once results are available,
          guide them in exploring how their Top 5 strengths connect to career
          fit, job search strategy, and personal branding.
        </p>
      )
    case 'BusinessCardCreation':
      return (
        <p>
          Candidates can request business cards. A Coach Lead will update the
          candidate record with three design options. The candidate will confirm
          their final selection and approve printing.
        </p>
      )
    case 'ExecutiveBio':
      return (
        <p>
          Use this service to help candidates develop a one-page executive bio.
          Provide structured feedback on narrative flow, tone, and alignment
          with the candidate’s career goals. Clarify how the bio differs from a
          resume (storytelling vs. chronology). If a candidate’s discussion
          shifts into resume or LinkedIn editing, use the admin tool to reassign
          feedback under those services for accuracy and tracking.
        </p>
      )
    case 'RecruiterGuidanceSupport':
      return (
        <p>
          Use this service to help candidates understand the recruiter
          relationship and improve their effectiveness in recruiter
          interactions. Provide actionable do’s and don’ts, and help candidates
          craft language for outreach and responses. If the session uncovers
          deeper needs (e.g., LinkedIn optimization, networking strategy),
          reassign the feedback to the appropriate service using the admin tool
          for tracking and clarity.
        </p>
      )
    case 'BoardSearch':
      return (
        <p>
          Use this service to help candidates explore and pursue
          executive/advisory board opportunities. Provide guidance on how to
          identify and prioritize target organizations, sharpen their board
          value proposition, and position themselves for recruitment.
        </p>
      )
    case 'ResumeWriting':
      return (
        <p>
          Use this service when a candidate needs a resume built from the ground
          up. Collect complete background information before drafting, and
          create a professional resume in a recruiter-friendly format.
        </p>
      )
  }
}

const ServiceRequestDetail = () => {
  const { detailItemId, detailType } = useCandidate()
  const { serviceRequests, isFetching, isLoading, isFetched } =
    useServiceRequests()
  const formatTime = useFormatTime()
  const currentServiceRequest =
    serviceRequests?.find(
      (serviceRequest) => serviceRequest.id == detailItemId,
    ) ||
    serviceRequests?.find(
      (serviceRequest) =>
        serviceRequest.requestType == `ServiceRequest::${detailType}`,
    )
  const detailRequestType = detailType ? `ServiceRequest::${detailType}` : null
  const detailDisplayName = formatDetailType(detailType) || 'Service'
  const starterServiceContext = {
    requestType: detailRequestType,
    displayName: detailDisplayName,
  }
  const activeServiceContext = currentServiceRequest || starterServiceContext

  const { completedReoccurringRequests } = currentServiceRequest || {}
  const tabTitle = activeServiceContext?.displayName
    ? `${activeServiceContext.displayName} Progress`
    : 'Service Progress'
  const isInitialLoading =
    !isFetched || ((isLoading || isFetching) && !currentServiceRequest)
  const isResearchQueueDetail = researchQueueTypes.includes(detailRequestType)

  const { coachCanInitiateServiceRequest } = useCandidate()

  if (isInitialLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loading className="h-10 w-10" />
      </div>
    )
  }

  return (
    <ServiceRequestContext.Provider value={activeServiceContext}>
      <TabNav defaultActiveTab="service_request_detail">
        <ButtonContainer className="justify-start gap-4">
          <TabButton name="service_request_detail" text={tabTitle} />
          <StatusIndicator />
        </ButtonContainer>
        <PanelContainer>
          <TabPanel name="service_request_detail">
            <div>
              <div className="my-4 text-lg">
                <ServiceIntroduction />
              </div>
              {completedReoccurringRequests?.length > 0 &&
                coachCanInitiateServiceRequest &&
                !serviceRequests?.some(
                  (sr) =>
                    sr.requestType === `ServiceRequest::${detailType}` &&
                    sr.status !== 'closed',
                ) && (
                  <div className="mb-6">
                    <DetailTable serviceRequest={starterServiceContext} />
                  </div>
                )}
              {completedReoccurringRequests?.length > 0 &&
                currentServiceRequest?.createdAt && (
                  <div className="mb-4">
                    <div className="mb-4 font-bold">
                      {formatTime(
                        moment(currentServiceRequest.createdAt, 'YYYY-MM-DD'),
                        'short',
                      )}
                    </div>
                  </div>
                )}
              {currentServiceRequest ? (
                <div className="mb-4">
                  <DetailTable serviceRequest={currentServiceRequest} />
                </div>
              ) : (
                <div className="mb-4">
                  {!isResearchQueueDetail && (
                    <DetailTable serviceRequest={starterServiceContext} />
                  )}
                </div>
              )}
              {completedReoccurringRequests
                ?.filter((request) => request.id !== currentServiceRequest?.id)
                .map((request, index) => (
                  <div className="mb-4" key={request.id || index}>
                    <div className="mb-4 font-bold">
                      {formatTime(
                        moment(request.createdAt, 'YYYY-MM-DD'),
                        'short',
                      )}
                    </div>
                    <DetailTable serviceRequest={request} />
                  </div>
                ))}
            </div>
          </TabPanel>
        </PanelContainer>
      </TabNav>
    </ServiceRequestContext.Provider>
  )
}

export default ServiceRequestDetail

const DetailTable = ({ serviceRequest }) => {
  return (
    <ServiceRequestContext.Provider value={serviceRequest}>
      <Table
        className="w-full border-separate"
        tableClass="rounded-2xl border border-silver"
      >
        <ServiceRequestHeading />
        <TableBody>
          <CreatedAtRow />
          <AssignmentRow />
          <NoShowRows />
          <MeetingRow />
          {researchQueueTypes.includes(serviceRequest?.requestType) && (
            <ResearchFeedbackRow />
          )}
          <MeetingFeedbackRow />
        </TableBody>
      </Table>
    </ServiceRequestContext.Provider>
  )
}

const ServiceRequestHeading = () => {
  const cells = [
    'Service Progress',
    'Status',
    'Due Date',
    'Documents',
    'Actions',
    'Invoiced Time (minutes)',
  ]
  return (
    <TableHeader className="bg-intoo-blue-medium-foo h-6">
      <TableRow className="bg-intoo-blue-medium text-lg hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}

const CreatedAtRow = () => {
  const serviceRequest = useServiceRequest()
  const {
    createdAt,
    message,
    displayName = 'Service',
    coachResumeUrl,
    candidateDocumentUrl,
    requestType = '',
    candidateDocumentLabel,
  } = serviceRequest

  const { detailType } = useCandidate()

  const isResearchQueueType = researchQueueTypes.includes(requestType)
  const hasElectronicRequestType =
    hasElectronicRequestTypes.includes(requestType)

  return (
    <StripedRow>
      <TableCell className="text-medium">{displayName} Requested</TableCell>
      <TableCell>
        {createdAt ? (
          <CompletedOnStatus completedAt={createdAt} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {candidateDocumentLabel && candidateDocumentUrl ? (
          <a href={candidateDocumentUrl} target="_blank" rel="noreferrer">
            {candidateDocumentLabel}
          </a>
        ) : coachResumeUrl ? (
          <a href={coachResumeUrl} target="_blank" rel="noreferrer">
            Candidate Resume
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      {message || createdAt ? (
        <TableCell>
          <div className="flex flex-col gap-2">
            <RevisionDialog type="Candidate" revisionData={serviceRequest}>
              View Request Note
            </RevisionDialog>
          </div>
        </TableCell>
      ) : !isResearchQueueType ? (
        <RequestCell>
          <div className="flex flex-col gap-2">
            {hasElectronicRequestType ? (
              <InitiateServiceRequestDialog
                requestType={detailType}
                initiationMode="electronic"
              >
                <Button
                  variant="link"
                  className="justify-start font-normal text-intoo-blue-medium hover:text-link"
                >
                  Open Platform Review
                </Button>
              </InitiateServiceRequestDialog>
            ) : null}
            <InitiateServiceRequestDialog
              requestType={detailType}
              initiationMode="meeting"
            >
              <Button
                variant="link"
                className="justify-start font-normal text-intoo-blue-medium hover:text-link"
              >
                Schedule Meeting For Candidate
              </Button>
            </InitiateServiceRequestDialog>
          </div>
        </RequestCell>
      ) : (
        <TableCell>&mdash;</TableCell>
      )}
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const ResearchFeedbackRow = () => {
  const { coachDocumentUrl, requestType } = useServiceRequest()
  const rowText =
    requestType === 'ServiceRequest::BusinessCardCreation'
      ? 'Proof Feedback'
      : 'Research Feedback'
  return (
    <StripedRow>
      <TableCell className="text-medium">{rowText}</TableCell>
      <TableCell>
        {coachDocumentUrl ? (
          <CompletedStatus>Completed</CompletedStatus>
        ) : (
          <PendingStatus>Pending</PendingStatus>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {coachDocumentUrl ? (
          <a href={coachDocumentUrl} target="_blank" rel="noreferrer">
            Download Research Report
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        <span>&mdash;</span>
      </TableCell>
    </StripedRow>
  )
}

const AssignmentRow = () => {
  const { assignedAt, assignmentDueDate, coachId, currentUserIsCoach } =
    useServiceRequest()
  const formatTime = useFormatTime()
  const displayAssignmentDue = assignmentDueDate
    ? formatTime(assignmentDueDate, 'short')
    : null
  return (
    <StripedRow>
      <TableCell className="text-medium">Coach Assigned</TableCell>
      <TableCell>
        {assignedAt ? (
          <CompletedOnStatus completedAt={assignedAt} />
        ) : currentUserIsCoach ? (
          <CompletedStatus>Assigned to You</CompletedStatus>
        ) : coachId ? (
          <PendingStatus>Assigned to Another Coach</PendingStatus>
        ) : (
          <PendingStatus>Incomplete</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {assignedAt ? (
          <span>&mdash;</span>
        ) : (
          displayAssignmentDue || <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        <span>&mdash;</span>
      </TableCell>
    </StripedRow>
  )
}

const NoShowRows = () => {
  const { cancelledOrNoShowEvents = [], pendingEvents = [] } =
    useServiceRequest()
  if (!cancelledOrNoShowEvents || pendingEvents.length > 0) return null

  return (
    <>
      {cancelledOrNoShowEvents.map((event, idx) => {
        const issue = event.cancelled
          ? 'Cancelled'
          : event.rescheduled
            ? 'Rescheduled'
            : 'No-Show'
        const invoiceableMins = event?.invoicedMinutes
        const invoiceId = event?.invoiceId
        const invoiceMinuteItemId = event?.invoiceMinuteItemId
        return (
          <StripedRow key={`no-show-${idx}`}>
            <TableCell className="text-medium">Meeting Issue</TableCell>
            <TableCell>
              <CalloutStatus>{issue}</CalloutStatus>
            </TableCell>
            <TableCell>&mdash;</TableCell>
            <TableCell>&mdash;</TableCell>
            <TableCell>
              {event.cancelled ? (
                <RescheduleMeetingDialog event={event}>
                  <Button
                    variant="link"
                    className="justify-start font-normal text-intoo-blue-medium hover:text-link"
                  >
                    Reschedule Meeting
                  </Button>
                </RescheduleMeetingDialog>
              ) : (
                <span>&mdash;</span>
              )}
            </TableCell>
            <TableCell>
              {invoiceableMins ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
                >
                  {invoiceableMins}
                </a>
              ) : (
                <span>&mdash;</span>
              )}
            </TableCell>
          </StripedRow>
        )
      })}
    </>
  )
}

const MeetingRow = () => {
  const {
    completedEvents = [],
    pendingEvents = [],
    meetingViaCalendly = false,
    completedAt,
  } = useServiceRequest()
  const formatTime = useFormatTime()
  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]
  const displayCompletedTime = currentCompletedEvent
    ? formatTime(currentCompletedEvent?.endTime, 'short')
    : null
  const scheduledForTime = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime)
    : null
  const scheduledForDate = currentPendingEvent
    ? formatTime(currentPendingEvent?.startTime, 'short')
    : null

  if (!meetingViaCalendly) {
    return (
      <StripedRow>
        <TableCell className="text-medium">Meeting Info</TableCell>
        <TableCell>
          <PendingStatus>N/A</PendingStatus>
        </TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  return (
    <StripedRow>
      <TableCell className="text-medium">Meeting Info</TableCell>
      <TableCell>
        {completedEvents?.length > 0 ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : pendingEvents?.length > 0 ? (
          <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
        ) : (
          <PendingStatus>
            {completedAt
              ? 'Cancelled by Coach After Closed'
              : 'Awaiting Candidate'}
          </PendingStatus>
        )}
      </TableCell>
      <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        <MeetingInfoCell />
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const MeetingInfoCell = () => {
  const serviceRequest = useServiceRequest()
  const {
    completedEvents = [],
    pendingEvents = [],
    coachRevisions = [],
    candidateId,
  } = serviceRequest

  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]

  // event has happened but there are no revisions, meaning perhaps they never met
  const showNoShowDialog = currentCompletedEvent && !coachRevisions?.length

  const invalidateQueryCache = () => {
    queryClient.invalidateQueries(['serviceRequests', candidateId])
  }

  const { joinUrl } = currentPendingEvent || {}

  if (showNoShowDialog)
    return (
      <NoShowDialog calendlyEvent={currentCompletedEvent}>
        Mark as No-Show
      </NoShowDialog>
    )
  if (currentPendingEvent) {
    return (
      <div className="flex flex-col gap-1">
        <a href={joinUrl} target="_blank" rel="noopener noreferrer">
          Join Meeting
        </a>
        <CancelMeetingModal
          serviceRequest={serviceRequest}
          onSuccess={invalidateQueryCache}
        >
          Cancel & Message Candidate
        </CancelMeetingModal>
        <NoShowDialog calendlyEvent={currentPendingEvent}>
          Mark as No-Show
        </NoShowDialog>
      </div>
    )
  }
  return <span>&mdash;</span>
}

const MeetingFeedbackRow = () => {
  const serviceRequest = useServiceRequest()
  const { detailType } = useCandidate()
  const formatTime = useFormatTime()
  const {
    dueAt,
    status,
    completedAt,
    invoicedMinutes: invoiceableMins,
    invoiceId,
    invoiceMinuteItemId,
    showInvoiceLink,
    meetingViaCalendly,
    displayStatus,
    coachNextTask,
    coachDocuments = [],
  } = serviceRequest || {}
  const displayDueAt = dueAt ? formatTime(dueAt, 'short') : null
  const isActionable =
    (meetingViaCalendly &&
      status == 'scheduled' &&
      displayStatus === 'Meeting Scheduled') ||
    (!meetingViaCalendly &&
      status === 'assigned' &&
      displayStatus != 'In Progress') ||
    (status == 'scheduled' &&
      displayStatus === 'Assigned to Coach' &&
      coachNextTask !== 'Send Follow-Up Message')
  const isCompleted = status == 'closed'

  const { requestType = '' } = useServiceRequest()
  const isResearchQueueType = researchQueueTypes.includes(requestType)
  return (
    <StripedRow>
      <TableCell className="text-medium">Meeting Feedback</TableCell>
      <TableCell>
        {isActionable ? (
          <CalloutStatus>Awaiting Coach Feedback</CalloutStatus>
        ) : isCompleted ? (
          <CompletedOnStatus completedAt={completedAt} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {(isActionable && displayDueAt) || <span>&mdash;</span>}
      </TableCell>
      <TableCell>
        {coachDocuments.length > 0 ? (
          <div className="flex flex-col space-y-2">
            {coachDocuments.map((doc, index) => (
              <a
                key={index}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-intoo-blue-medium hover:underline"
              >
                {doc.fileName || `Document ${index + 1}`}
              </a>
            ))}
          </div>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      {isCompleted ? (
        <TableCell>
          <ViewCoachFeedbackDialog serviceRequest={serviceRequest}>
            View Coach Feedback
          </ViewCoachFeedbackDialog>
        </TableCell>
      ) : !serviceRequest?.id && !isResearchQueueType ? (
        <TableCell>
          <ServiceRequestFeedbackDialog
            createRequest={true}
            detailType={detailType}
            startedAndClosed={true}
          >
            Initiate/Close For Candidate And Provide Feedback
          </ServiceRequestFeedbackDialog>
        </TableCell>
      ) : isResearchQueueType && !serviceRequest?.id ? (
        <TableCell>&mdash;</TableCell>
      ) : (
        <TableCell>
          <ServiceRequestFeedbackDialog>
            Add Feedback and Close Service
          </ServiceRequestFeedbackDialog>
        </TableCell>
      )}
      <TableCell>
        {invoiceableMins ? (
          showInvoiceLink ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={`/coach/invoices/${invoiceId}?highlight=${invoiceMinuteItemId}`}
            >
              {invoiceableMins}
            </a>
          ) : (
            <span
              className="cursor-not-allowed text-gray-400"
              title="You do not have access to this invoice."
            >
              {invoiceableMins}
            </span>
          )
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
    </StripedRow>
  )
}

const StripedRow = ({ children, className, ...props }) => {
  return (
    <TableRow
      {...props}
      className={cn(
        'odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

const StatusIndicator = ({ serviceRequest }) => {
  // If serviceRequest is passed, use its fields; otherwise, use context
  const context = useServiceRequest()
  const { status, displayStatus, completedAt, meetingViaCalendly } =
    serviceRequest || context

  return (
    <div>
      {isActionable(status, meetingViaCalendly) ? (
        <CalloutStatus>{displayStatus}</CalloutStatus>
      ) : completedAt ? (
        <CompletedOnStatus date={completedAt} />
      ) : (
        <PendingStatus>{displayStatus}</PendingStatus>
      )}
    </div>
  )
}

const isActionable = (status, meetingViaCalendly) => {
  return (
    ['revision_requested', 'scheduled'].includes(status) ||
    (!meetingViaCalendly && status === 'assigned')
  )
}

export const RequestCell = ({ revisionNumber, children }) => {
  const { coachCanInitiateServiceRequest } = useCandidate()
  const canInitiate = revisionNumber
    ? revisionNumber === 0 && coachCanInitiateServiceRequest
    : coachCanInitiateServiceRequest
  if (canInitiate) return <TableCell>{children}</TableCell>
  return <TableCell>&mdash;</TableCell>
}
